import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PhotoCapture from "@/components/PhotoCapture";
import UserAvatar from "@/components/UserAvatar";
import OrganizerBadge from "@/components/OrganizerBadge";
import { AVATAR_STYLES, AVATAR_BACKGROUNDS, AVATAR_BRANDS, PALA_BRANDS as DEFAULT_PALA_BRANDS, APPAREL_BRANDS as DEFAULT_APPAREL_BRANDS, SHOES_BRANDS as DEFAULT_SHOES_BRANDS } from "@/lib/avatar";
import { UserCircle, FloppyDisk, Heart, Phone, FirstAid, Trophy, Globe, Sparkle, Sneaker, TShirt, Tag } from "@phosphor-icons/react";
import { COUNTRIES, STATES_BY_COUNTRY, CATEGORIES } from "@/lib/locations";
import CountrySelect from "@/components/CountrySelect";
import MultiClubSelect from "@/components/MultiClubSelect";
import OnboardingTour from "@/components/OnboardingTour";
import MyUsageCard from "@/components/MyUsageCard";
import { emitStatsChanged } from "@/lib/statsBus";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileEdit() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", birth_date: "",
    blood_type: "", allergies: "", medical_conditions: "",
    emergency_contact_name: "", emergency_contact_phone: "", insurance_number: "",
    category: "", experience_years: "", country: "", state: "",
    position: "", dominant_hand: "", sport: "padel",
    avatar_style: "", avatar_seed: "", avatar_bg: "", avatar_brand: "",
    gear_pala_brand: "", gear_pala_model: "", gear_apparel_brand: "", gear_shoes_brand: "", gear_accessories: "",
    gear_wristband_brand: "", gear_accessory_color: "", gear_sponsor: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [profClubIds, setProfClubIds] = useState([]);
  const [savingClubs, setSavingClubs] = useState(false);
  const [gearCatalog, setGearCatalog] = useState({
    pala: DEFAULT_PALA_BRANDS,
    apparel: DEFAULT_APPAREL_BRANDS,
    shoes: DEFAULT_SHOES_BRANDS,
    wristband: [],
    accessory: [],
    color: [],
    sponsor: [],
    avatar_brand: AVATAR_BRANDS,
  });

  useEffect(() => {
    // Load admin-editable gear catalog. Falls back to the hardcoded defaults.
    api.get("/gear-catalog").then(({ data }) => {
      const merge = (backendArr, defaults) => {
        const arr = Array.isArray(backendArr) ? backendArr : [];
        return arr.length > 0 ? arr : defaults;
      };
      // Normalize avatar_brand items to expose {primary, accent, short, logo}
      // used by the existing button UI (fallback: color/text -> primary/accent).
      const mapAvatarBrand = (arr) => (arr || []).map((it) => ({
        ...it,
        primary: it.primary || it.color || "#0A0A0A",
        accent: it.accent || it.text || "#FFFFFF",
      }));
      setGearCatalog({
        pala: merge(data.pala, DEFAULT_PALA_BRANDS),
        apparel: merge(data.apparel, DEFAULT_APPAREL_BRANDS),
        shoes: merge(data.shoes, DEFAULT_SHOES_BRANDS),
        wristband: data.wristband || [],
        accessory: data.accessory || [],
        color: data.color || [],
        sponsor: data.sponsor || [],
        avatar_brand: mapAvatarBrand(data.avatar_brand?.length ? data.avatar_brand : AVATAR_BRANDS),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "profesor") {
      api.get("/clubs").then(({ data }) => setClubs(data || [])).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    api.get(`/users/${user?.id}`).then(({ data }) => {
      setForm({
        name: data.name || "", email: data.email || "", phone: data.phone || "",
        address: data.address || "", birth_date: data.birth_date || "",
        blood_type: data.blood_type || "", allergies: data.allergies || "",
        medical_conditions: data.medical_conditions || "",
        emergency_contact_name: data.emergency_contact_name || "",
        emergency_contact_phone: data.emergency_contact_phone || "",
        insurance_number: data.insurance_number || "",
        category: data.category || "",
        experience_years: data.experience_years ?? "",
        playing_since: data.playing_since || "",
        country: data.country || "",
        state: data.state || "",
        position: data.position || "",
        dominant_hand: data.dominant_hand || "",
        sport: data.sport || "padel",
        avatar_style: data.avatar_style || "personas",
        avatar_seed: data.avatar_seed || data.name || "",
        avatar_bg: data.avatar_bg || "ccff00",
        avatar_brand: data.avatar_brand || "",
        gear_pala_brand: data.gear_pala_brand || "",
        gear_pala_model: data.gear_pala_model || "",
        gear_apparel_brand: data.gear_apparel_brand || "",
        gear_shoes_brand: data.gear_shoes_brand || "",
        gear_accessories: data.gear_accessories || "",
        gear_wristband_brand: data.gear_wristband_brand || "",
        gear_accessory_color: data.gear_accessory_color || "",
        gear_sponsor: data.gear_sponsor || "",
      });
      // Load current profesor club_ids
      if ((data.role || user?.role) === "profesor") {
        const cids = Array.isArray(data.club_ids) && data.club_ids.length
          ? data.club_ids
          : (data.club_id ? [data.club_id] : []);
        setProfClubIds(cids);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.experience_years === "") payload.experience_years = null;
      else payload.experience_years = parseFloat(payload.experience_years);
      if (!payload.playing_since) payload.playing_since = null;
      if (!payload.category) payload.category = null;
      if (!payload.country) payload.country = null;
      if (!payload.state) payload.state = null;
      if (!payload.position) payload.position = null;
      if (!payload.dominant_hand) payload.dominant_hand = null;
      await api.put("/users/profile/update", payload);
      toast.success("Perfil actualizado!");
      emitStatsChanged("profile:updated", { userId: user?.id });
      checkAuth();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      await api.post("/users/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto actualizada!");
      emitStatsChanged("profile:photo-updated", { userId: user?.id });
      checkAuth();
    } catch { toast.error("Error al subir foto"); }
  };

  const u = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="profile-edit-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Mi Cuenta</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Perfil</h1>
      </div>

      {/* Photo */}
      <div className="card-tactical p-6 flex items-center gap-6" data-testid="profile-photo-section">
        {user?.photo_path ? (
          <img src={`${API_URL}/api/users/photo/${user.photo_path}`} alt="" className="w-20 h-20 object-cover border-2 border-[#CCFF00]" />
        ) : (
          <UserAvatar user={{ ...user, ...form }} size={80} className="border-2 border-[#CCFF00]" testId="profile-current-avatar" />
        )}
        <div>
          <p className="text-white font-semibold text-lg">{user?.name}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider">{user?.role}</p>
          <div className="mt-2">
            <OrganizerBadge user={user} variant="tile" />
          </div>
          <div className="mt-2">
            <PhotoCapture onPhotoTaken={handlePhotoUpload} />
          </div>
        </div>
      </div>

      {/* Avatar Generator (always available; if user has photo, photo takes priority but avatar is still rendered) */}
      <div className="card-tactical p-6" data-testid="avatar-editor">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Sparkle size={16} /> Tu Avatar 3D</p>
        <p className="text-[11px] text-gray-500 mb-4">
          {user?.photo_path
            ? "Tu foto se muestra como prioridad. El avatar 3D aparece en el panel de identidad y al lado de tu nombre cuando no hay foto."
            : "Personaliza tu avatar (se usa cuando no tienes foto subida)."}
        </p>
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
            {/* Force avatar render (ignore photo_path) so the user can preview their selection live */}
            <UserAvatar user={{ ...user, ...form, photo_path: null }} size={120} className="border-2 border-[#CCFF00]" testId="avatar-preview" />
            <div className="space-y-4 flex-1">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Estilo</Label>
                <div className="grid grid-cols-4 gap-2">
                  {AVATAR_STYLES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => u("avatar_style", s.id)}
                      className={`px-2 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors relative ${form.avatar_style === s.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                      data-testid={`avatar-style-${s.id}`}
                    >
                      {s.label}
                      {s.is3d && <span className="absolute -top-1.5 -right-1.5 bg-[#FF6B00] text-black text-[7px] font-black px-1 py-0.5 leading-none">3D</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Color de fondo</Label>
                <div className="flex flex-wrap gap-2">
                  {AVATAR_BACKGROUNDS.map((bg) => (
                    <button
                      key={bg.id}
                      type="button"
                      onClick={() => u("avatar_bg", bg.id)}
                      title={bg.label}
                      className={`w-9 h-9 border-2 transition-transform hover:scale-110 ${form.avatar_bg === bg.id ? "border-[#CCFF00] ring-2 ring-[#CCFF00]/30" : "border-white/10"}`}
                      style={{ backgroundColor: bg.hex }}
                      data-testid={`avatar-bg-${bg.id}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Semilla (cambia para variar el avatar)</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.avatar_seed}
                    onChange={(e) => u("avatar_seed", e.target.value)}
                    placeholder="Tu nombre o texto único"
                    className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#CCFF00]"
                    data-testid="avatar-seed-input"
                  />
                  <Button
                    type="button"
                    onClick={() => u("avatar_seed", Math.random().toString(36).slice(2, 12))}
                    variant="outline"
                    className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs h-10 shrink-0"
                    data-testid="avatar-randomize"
                  >
                    🎲 Aleatorio
                  </Button>
                </div>
                {/* Quick variations gallery */}
                <AvatarVariations form={form} onPick={(seed) => u("avatar_seed", seed)} />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Patrocinador / Marca (vestimenta)</Label>
                <p className="text-[10px] text-gray-500 mb-2">Aparece como insignia en la esquina del avatar</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => u("avatar_brand", "")}
                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${!form.avatar_brand ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                    data-testid="avatar-brand-none"
                  >
                    Sin marca
                  </button>
                  {gearCatalog.avatar_brand.map((br) => (
                    <button
                      key={br.id}
                      type="button"
                      onClick={() => u("avatar_brand", br.id)}
                      className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors flex items-center justify-between gap-2 ${form.avatar_brand === br.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                      style={{ backgroundColor: form.avatar_brand === br.id ? br.primary : "#0A0A0A", color: form.avatar_brand === br.id ? br.accent : "#9CA3AF" }}
                      data-testid={`avatar-brand-pick-${br.id}`}
                    >
                      <span>{br.name}</span>
                      {br.logo ? (
                        <img src={br.logo} alt={br.name} className="h-5 w-auto max-w-[40px] object-contain bg-white p-0.5" />
                      ) : (
                        <span
                          className="px-1.5 py-0.5 text-[9px] font-bold border"
                          style={{ backgroundColor: br.primary, color: br.accent, borderColor: "#0A0A0A" }}
                        >
                          {br.short}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Personal Data */}
      <div className="card-tactical p-6" data-testid="personal-data">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-4 flex items-center gap-2"><UserCircle size={16} /> Datos Personales</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
            <Input data-testid="profile-name" value={form.name} onChange={(e) => u("name", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email</Label>
            <Input data-testid="profile-email" value={form.email} onChange={(e) => u("email", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
            <Input data-testid="profile-phone" value={form.phone} onChange={(e) => u("phone", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha de Nacimiento</Label>
            <Input data-testid="profile-birth" type="date" value={form.birth_date} onChange={(e) => u("birth_date", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div className="md:col-span-2"><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Direccion</Label>
            <Input data-testid="profile-address" value={form.address} onChange={(e) => u("address", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Pais</Label>
            <CountrySelect
              value={form.country}
              onChange={(v) => { u("country", v); u("state", ""); }}
              testid="profile-country"
            />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Estado</Label>
            <Select value={form.state} onValueChange={(v) => u("state", v)} disabled={!form.country}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11 disabled:opacity-50" data-testid="profile-state"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                {(STATES_BY_COUNTRY[form.country] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Sports Data */}
      <div className="card-tactical p-6" data-testid="sports-data">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-4 flex items-center gap-2"><Trophy size={16} /> Datos Deportivos</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {user?.role === "jugador" && (
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                  Categoria {form.category && <span className="text-[#CCFF00]">(asignada)</span>}
                </Label>
                {form.category ? (
                  <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] h-11 flex items-center px-4 font-bold uppercase tracking-wide text-sm" data-testid="profile-category-locked">
                    {CATEGORIES.find((c) => c.value === form.category)?.label || form.category}
                    <span className="ml-auto text-[10px] text-gray-400 normal-case tracking-normal font-normal">Solo admin puede cambiarla</span>
                  </div>
                ) : (
                  <>
                    <Select value={form.category} onValueChange={(v) => u("category", v)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="profile-category"><SelectValue placeholder="Selecciona categoria (solo una vez)" /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-orange-400 mt-1.5">Solo podras elegir tu categoria una vez. Para cambios posteriores contacta al admin.</p>
                  </>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                {user?.role === "profesor" ? "Tiempo dando clinicas (anos)" : "Tiempo jugando (anos)"}
              </Label>
              <Input
                data-testid="profile-experience"
                type="number"
                min="0"
                max="80"
                step="0.5"
                value={form.experience_years}
                onChange={(e) => u("experience_years", e.target.value)}
                placeholder="Ej: 2.5"
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600"
              />
              <p className="text-[11px] text-gray-500 mt-1.5">Usa decimales para meses (0.5 = 6 meses).</p>
            </div>
            {user?.role === "profesor" && (
              <div data-testid="profile-teacher-clubs-block">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                  Mis clubes (donde imparto clases)
                </Label>
                <MultiClubSelect
                  clubs={clubs}
                  value={profClubIds}
                  onChange={setProfClubIds}
                  testid="profile-teacher-clubs"
                  emptyLabel="Sin clubes asignados"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    onClick={async () => {
                      setSavingClubs(true);
                      try {
                        await api.put("/me/clubs", { club_ids: profClubIds });
                        toast.success("Clubes actualizados");
                        checkAuth();
                      } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                      finally { setSavingClubs(false); }
                    }}
                    disabled={savingClubs}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-8"
                    data-testid="profile-save-teacher-clubs"
                  >
                    {savingClubs ? "Guardando…" : "Guardar clubes"}
                  </Button>
                  <p className="text-[10px] text-gray-500">Selecciona uno o varios clubes donde impartas clases.</p>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                Jugando desde <span className="text-gray-600">(fecha de inicio)</span>
              </Label>
              <Input
                data-testid="profile-playing-since"
                type="date"
                value={form.playing_since}
                onChange={(e) => u("playing_since", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]"
              />
              {form.playing_since && (() => {
                const start = new Date(form.playing_since);
                if (isNaN(start.getTime())) return null;
                const ms = Date.now() - start.getTime();
                const years = ms / (365.25 * 24 * 3600 * 1000);
                if (years < 0) return <p className="text-[11px] text-red-400 mt-1.5">Fecha futura — ajusta el valor.</p>;
                const whole = Math.floor(years);
                const months = Math.floor((years - whole) * 12);
                const label = whole === 0 ? `${months} mes${months === 1 ? "" : "es"} jugando padel` : `${whole} año${whole === 1 ? "" : "s"} y ${months} mes${months === 1 ? "" : "es"} jugando padel`;
                return <p className="text-[11px] text-[#CCFF00] mt-1.5" data-testid="profile-years-derived">Llevas {label}.</p>;
              })()}
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Posicion</Label>
              <Select value={form.position || "__none"} onValueChange={(v) => u("position", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="profile-position"><SelectValue placeholder="Selecciona posicion" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="__none">Sin definir</SelectItem>
                  <SelectItem value="drive">Drive (Derecha)</SelectItem>
                  <SelectItem value="reves">Reves (Izquierda)</SelectItem>
                  <SelectItem value="ambos">Ambas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Mano Dominante</Label>
              <Select value={form.dominant_hand || "__none"} onValueChange={(v) => u("dominant_hand", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="profile-dominant-hand"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="__none">Sin definir</SelectItem>
                  <SelectItem value="derecha">Derecha</SelectItem>
                  <SelectItem value="izquierda">Izquierda</SelectItem>
                  <SelectItem value="ambidiestro">Ambidiestro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Deporte</Label>
              <Select value={form.sport || "padel"} onValueChange={(v) => u("sport", v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="profile-sport"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="padel">🎾 Pádel</SelectItem>
                  <SelectItem value="tenis">🎾 Tenis</SelectItem>
                  <SelectItem value="ambos">🎾 Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

      {/* Mi Equipamiento (real-world gear) */}
      <div className="card-tactical p-6" data-testid="gear-section">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Trophy size={16} /> Mi Equipamiento</p>
        <p className="text-[11px] text-gray-500 mb-4">Marca tu kit oficial: pala, ropa, calzado y accesorios. Aparecerá en tu Inicio.</p>
        <div className="space-y-5">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Trophy size={14} /> Pala (marca)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              <button type="button" onClick={() => u("gear_pala_brand", "")}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_pala_brand ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                data-testid="gear-pala-none">Ninguna</button>
              {gearCatalog.pala.map((b) => (
                <button key={b.id} type="button" onClick={() => u("gear_pala_brand", b.id)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${form.gear_pala_brand === b.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                  style={{ backgroundColor: form.gear_pala_brand === b.id ? b.color : "#0A0A0A", color: form.gear_pala_brand === b.id ? b.text : "#9CA3AF" }}
                  data-testid={`gear-pala-${b.id}`}
                >{b.name}</button>
              ))}
            </div>
            <Input data-testid="gear-pala-model" value={form.gear_pala_model} onChange={(e) => u("gear_pala_model", e.target.value)}
              placeholder="Modelo (ej: Vertex 04 Control, Pure Aero...)"
              className="bg-[#0A0A0A] border-white/20 text-white h-10 mt-3 focus:border-[#CCFF00] placeholder:text-gray-600" />
          </div>

          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><TShirt size={14} /> Ropa (marca)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              <button type="button" onClick={() => u("gear_apparel_brand", "")}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_apparel_brand ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                data-testid="gear-apparel-none">Ninguna</button>
              {gearCatalog.apparel.map((b) => (
                <button key={b.id} type="button" onClick={() => u("gear_apparel_brand", b.id)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${form.gear_apparel_brand === b.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                  style={{ backgroundColor: form.gear_apparel_brand === b.id ? b.color : "#0A0A0A", color: form.gear_apparel_brand === b.id ? b.text : "#9CA3AF" }}
                  data-testid={`gear-apparel-${b.id}`}
                >{b.name}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Sneaker size={14} /> Calzado (marca)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              <button type="button" onClick={() => u("gear_shoes_brand", "")}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_shoes_brand ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                data-testid="gear-shoes-none">Ninguno</button>
              {gearCatalog.shoes.map((b) => (
                <button key={b.id} type="button" onClick={() => u("gear_shoes_brand", b.id)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${form.gear_shoes_brand === b.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                  style={{ backgroundColor: form.gear_shoes_brand === b.id ? b.color : "#0A0A0A", color: form.gear_shoes_brand === b.id ? b.text : "#9CA3AF" }}
                  data-testid={`gear-shoes-${b.id}`}
                >{b.name}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Tag size={14} /> Accesorios</Label>
            <Input data-testid="gear-accessories" value={form.gear_accessories} onChange={(e) => u("gear_accessories", e.target.value)}
              placeholder="Ej: Muñequera HEAD, Gorra Bullpadel, Overgrip Babolat..."
              className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#CCFF00] placeholder:text-gray-600" />
          </div>

          {gearCatalog.wristband.length > 0 && (
            <div data-testid="gear-wristband-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Tag size={14} /> Muñequera (marca)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <button type="button" onClick={() => u("gear_wristband_brand", "")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_wristband_brand ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                  data-testid="gear-wristband-none">Ninguna</button>
                {gearCatalog.wristband.map((b) => (
                  <button key={b.id} type="button" onClick={() => u("gear_wristband_brand", b.id)}
                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${form.gear_wristband_brand === b.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                    style={{ backgroundColor: form.gear_wristband_brand === b.id ? b.color : "#0A0A0A", color: form.gear_wristband_brand === b.id ? b.text : "#9CA3AF" }}
                    data-testid={`gear-wristband-${b.id}`}
                  >{b.name}</button>
                ))}
              </div>
            </div>
          )}

          {gearCatalog.color.length > 0 && (
            <div data-testid="gear-color-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Tag size={14} /> Color favorito de uniforme</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                <button type="button" onClick={() => u("gear_accessory_color", "")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_accessory_color ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                  data-testid="gear-color-none">Ninguno</button>
                {gearCatalog.color.map((c) => (
                  <button key={c.id} type="button" onClick={() => u("gear_accessory_color", c.id)}
                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${form.gear_accessory_color === c.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                    style={{ backgroundColor: form.gear_accessory_color === c.id ? c.color : "#0A0A0A", color: form.gear_accessory_color === c.id ? c.text : "#9CA3AF" }}
                    data-testid={`gear-color-${c.id}`}
                  >{c.name}</button>
                ))}
              </div>
            </div>
          )}

          {gearCatalog.sponsor.length > 0 && (
            <div data-testid="gear-sponsor-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block flex items-center gap-2"><Tag size={14} /> Patrocinador (marca principal)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <button type="button" onClick={() => u("gear_sponsor", "")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border ${!form.gear_sponsor ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                  data-testid="gear-sponsor-none">Sin patrocinador</button>
                {gearCatalog.sponsor.map((s) => (
                  <button key={s.id} type="button" onClick={() => u("gear_sponsor", s.id)}
                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors flex items-center gap-2 ${form.gear_sponsor === s.id ? "ring-2 ring-[#CCFF00] border-[#CCFF00]" : "border-white/10 hover:border-[#CCFF00]/30"}`}
                    style={{ backgroundColor: form.gear_sponsor === s.id ? s.color : "#0A0A0A", color: form.gear_sponsor === s.id ? s.text : "#9CA3AF" }}
                    data-testid={`gear-sponsor-${s.id}`}
                  >
                    {s.logo && <img src={s.logo} alt={s.name} className="h-4 w-auto max-w-[24px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                    <span className="truncate">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Medical Data */}
      <div className="card-tactical p-6" data-testid="medical-data">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-red-400 mb-4 flex items-center gap-2"><FirstAid size={16} /> Datos Medicos</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Tipo de Sangre</Label>
            <Select value={form.blood_type} onValueChange={(v) => u("blood_type", v)}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="profile-blood"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Numero de Seguro</Label>
            <Input data-testid="profile-insurance" value={form.insurance_number} onChange={(e) => u("insurance_number", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div className="md:col-span-2"><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Alergias</Label>
            <Input data-testid="profile-allergies" value={form.allergies} onChange={(e) => u("allergies", e.target.value)} placeholder="Ej: penicilina, mariscos..." className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" /></div>
          <div className="md:col-span-2"><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Condiciones Medicas</Label>
            <Input data-testid="profile-conditions" value={form.medical_conditions} onChange={(e) => u("medical_conditions", e.target.value)} placeholder="Ej: diabetes, hipertension..." className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" /></div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="card-tactical p-6" data-testid="emergency-contact">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-yellow-400 mb-4 flex items-center gap-2"><Phone size={16} /> Contacto de Emergencia</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
            <Input data-testid="profile-emergency-name" value={form.emergency_contact_name} onChange={(e) => u("emergency_contact_name", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
          <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
            <Input data-testid="profile-emergency-phone" value={form.emergency_contact_phone} onChange={(e) => u("emergency_contact_phone", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
        </div>
      </div>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full py-6" data-testid="save-profile-btn">
        <FloppyDisk size={18} className="mr-2" /> {saving ? "Guardando..." : "Guardar Perfil"}
      </Button>

      {/* Onboarding Tour Re-play */}
      <div className="card-tactical p-6" data-testid="onboarding-replay-section">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Ayuda</p>
        <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight">Tour de bienvenida</h3>
        <p className="text-xs text-gray-500 mt-1 mb-4">Revisa las 3 pantallas clave: Marcador, Comunidad y DYD Run.</p>
        <Button
          onClick={() => setShowTour(true)}
          variant="outline"
          className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase tracking-wider"
          data-testid="replay-onboarding-btn"
        >
          <Sparkle size={16} className="mr-2" weight="fill" /> Ver de nuevo el tour
        </Button>
      </div>

      {/* Mi tiempo en la plataforma */}
      <MyUsageCard />

      <OnboardingTour open={showTour} onClose={() => setShowTour(false)} variant="manual" />

      {/* ARCO Rights - Mexican privacy law Art. 16 LFPDPPP */}
      <div className="card-tactical p-6 border-red-500/20" data-testid="arco-rights">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Privacidad</p>
        <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight">Derechos ARCO</h3>
        <p className="text-xs text-gray-500 mt-1 mb-4">Acceso, Rectificacion, Cancelacion y Oposicion (Art. 16 LFPDPPP)</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={async () => {
            try {
              const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/export`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
              if (!r.ok) throw new Error("export failed");
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = `dyd-mis-datos.json`; a.click();
              URL.revokeObjectURL(url);
              toast.success("Datos descargados");
            } catch { toast.error("Error al exportar"); }
          }} variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5 uppercase text-xs font-bold flex-1" data-testid="arco-export-btn">
            Descargar mis datos (JSON)
          </Button>
          <Button onClick={async () => {
            const ok = window.confirm("Esta accion eliminara tu cuenta y anonimizara tu historial. No se puede deshacer. Continuar?");
            if (!ok) return;
            const ok2 = window.prompt('Escribe "ELIMINAR" para confirmar');
            if (ok2 !== "ELIMINAR") { toast.error("Confirmacion incorrecta"); return; }
            try {
              await api.delete("/users/me");
              toast.success("Cuenta eliminada");
              localStorage.removeItem("token");
              window.location.href = "/login";
            } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
          }} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 uppercase text-xs font-bold flex-1" data-testid="arco-delete-btn">
            Eliminar mi cuenta
          </Button>
        </div>
        <p className="text-[10px] text-gray-600 mt-3">Al eliminar: se reemplaza tu nombre por "Usuario eliminado" en partidos historicos para preservar estadisticas de otros jugadores. Notificaciones y sesiones se purgan.</p>
      </div>
    </div>
  );
}

// =====================================================
// AvatarVariations — gallery of 6 quick-pick avatars
// using the user's current style + bg, varying only seed.
// =====================================================
function AvatarVariations({ form, onPick }) {
  const [seeds, setSeeds] = useState(() => makeSeeds());
  function makeSeeds() {
    return Array.from({ length: 6 }).map(() => Math.random().toString(36).slice(2, 12));
  }
  return (
    <div className="mt-4" data-testid="avatar-variations">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Variaciones rápidas (toca una para elegir)</Label>
        <button type="button" onClick={() => setSeeds(makeSeeds())} className="text-[10px] text-[#CCFF00] hover:underline uppercase tracking-wider" data-testid="avatar-variations-refresh">↻ Otras</button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {seeds.map((seed) => {
          const candidate = { ...form, avatar_seed: seed, photo_path: null };
          const active = form.avatar_seed === seed;
          return (
            <button
              key={seed}
              type="button"
              onClick={() => onPick(seed)}
              className={`relative aspect-square overflow-hidden border-2 transition-all hover:scale-105 ${active ? "border-[#CCFF00] ring-2 ring-[#CCFF00]/40" : "border-white/10 hover:border-[#CCFF00]/40"}`}
              style={{ backgroundColor: form.avatar_bg ? `#${form.avatar_bg}` : "#1A1A1A" }}
              data-testid={`avatar-variation-${seed}`}
              title={`Usar variación ${seed.slice(0, 4)}`}
            >
              <UserAvatar user={candidate} size={80} className="w-full h-full" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
